import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import ImportExportOutlinedIcon from '@mui/icons-material/ImportExportOutlined';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import PageMeta from 'components/seo/PageMeta';
import ErrorBoundary from 'components/feedback/ErrorBoundary';
import { ROUTES } from 'config/routes';
import { useStoreAdminContext } from 'context/StoreAdminContext';
import { useTenant } from 'hooks/useTenant';
import { useStorefrontContext } from 'context/StorefrontContext';

const adminLinks = [
  { label: 'Resumen', path: ROUTES.ADMIN, icon: DashboardOutlinedIcon },
  { label: 'Apariencia', path: ROUTES.ADMIN_APPEARANCE, icon: PaletteOutlinedIcon },
  { label: 'Ingreso', path: ROUTES.ADMIN_AUTH_PAGE, icon: LoginOutlinedIcon },
  { label: 'Layout', path: ROUTES.ADMIN_LAYOUT, icon: ViewQuiltOutlinedIcon },
  { label: 'Navegación', path: ROUTES.ADMIN_NAVIGATION, icon: MenuOutlinedIcon },
  { label: 'Catálogo', path: ROUTES.ADMIN_CATALOG, icon: Inventory2OutlinedIcon },
  { label: 'Funcionalidades', path: ROUTES.ADMIN_FEATURES, icon: TuneOutlinedIcon },
  { label: 'Migración', path: ROUTES.ADMIN_CONFIG_PACK, icon: ImportExportOutlinedIcon },
];

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const { displayName, logout } = useStoreAdminContext();
  const { tenantCode, tenant } = useTenant();
  const { theme: storeTheme } = useStorefrontContext();
  const isLayoutEditor = location.pathname === ROUTES.ADMIN_LAYOUT;

  const tenantLabel = [tenantCode, storeTheme?.siteName || tenant.siteName]
    .filter(Boolean)
    .join(' · ');

  const sidebar = (
    <Stack component="nav" aria-label="administración tienda" spacing={0.75} sx={{ width: { md: 220 } }}>
      {adminLinks.map((link) => {
        const selected = location.pathname === link.path;
        const Icon = link.icon;
        return (
          <Box
            key={link.path}
            component={RouterLink}
            to={link.path}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.25,
              py: 1.1,
              borderRadius: 2,
              textDecoration: 'none',
              color: selected ? 'primary.main' : 'text.primary',
              bgcolor: selected ? 'rgba(25, 118, 210, 0.1)' : 'background.paper',
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              transition: 'border-color 160ms ease, background-color 160ms ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: selected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <Icon fontSize="small" />
            <Typography variant="body2" fontWeight={selected ? 700 : 600}>
              {link.label}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );

  if (isLayoutEditor) {
    return (
      <>
        <PageMeta title="Layout — Página de inicio" />
        <ErrorBoundary variant="page">
          <Outlet />
        </ErrorBoundary>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Administración de tienda" />
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
            Admin tienda
          </Typography>
          {tenantLabel && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={tenantLabel}
              sx={{ mr: 'auto' }}
            />
          )}
          {!tenantLabel && <Box sx={{ flexGrow: 1 }} />}
          {displayName && (
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
          )}
          <Button component={RouterLink} to={ROUTES.HOME} color="inherit">
            Ver tienda
          </Button>
          <Button color="inherit" onClick={logout}>
            Salir
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
          {sidebar}
          <Box flexGrow={1}>
            <ErrorBoundary variant="page">
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default AdminLayout;
